import { Fragment, useState } from 'react';
import { IconButton, Table, TableBody, TableHead, TableRow, Tooltip } from '@mui/material';
import { KeyboardArrowDown, KeyboardArrowUp } from '@mui/icons-material';

import GeneralTableBox from '../table/GeneralTableBox';
import HeadTableCell from '../table/HeadTableCell';
import SortableHeadTableCell from '../table/SortableHeadTableCell';
import PaginationTable from '../table/PaginationTable';
import RowTableCell from '../table/RowTableCell';
import NoDataSkeleton from '../skeletons/NoDataSkeleton';
import TableBodySkeleton from '../skeletons/TableBodySkeleton';
import MaterialRowDetails from './MaterialRowDetails';
import {
  COLUMN_LABELS,
  EXPAND_COLUMN_WIDTH,
  MATERIAL_REPORT_PAGE_SIZE,
  MATERIAL_TABLE_TEXTS as TEXTS,
  TEXT_COLUMNS,
} from './materialPresentationConstants';
import { getMaterialCellSx as getCellSx } from '../../styles/getMaterialCellSx';
import { getMaterialColumnWidth as getColumnWidth } from '../../styles/getMaterialColumnWidth';
import { SERVER_ROWS_PER_PAGE_OPTIONS } from '../../constants/paginationConstants';
import { materialCell } from '../../../utils/materialDisplay';

/**
 * Tabla de reportes de materiales con el formato de las tablas de Finanzas.
 * @param {Object} props
 * @param {Array<Object>} [props.rows] Renglones ya calculados por el backend.
 * @param {Array<string>} props.columns Claves de columna a mostrar.
 * @param {number} [props.total] Total de renglones del conjunto filtrado.
 * @param {number} [props.page] Página actual (base 1).
 * @param {Function} [props.onPage] Recibe la nueva página; si falta, no se pagina.
 * @param {number} [props.rowsPerPage] Tamaño de la página consultada.
 * @param {Function} [props.onRowsPerPageChange] Cambia el tamaño de página.
 * @param {Object} [props.totals] Totales del conjunto filtrado.
 * @param {Function} [props.onSelect] Recibe el renglón al hacer clic.
 * @param {boolean} [props.isLoading] Muestra skeleton mientras carga.
 * @param {boolean} [props.hasMaxHeight] Limita el alto de la tabla.
 * @param {{key: string, direction: string}|null} [props.sort] Orden vigente.
 * @param {Function} [props.onSort] Recibe la columna clickeada; sin él no se ordena.
 * @returns {JSX.Element}
 */
const MaterialReportTable = ({
  rows = [],
  columns,
  total,
  page = 1,
  onPage,
  rowsPerPage = MATERIAL_REPORT_PAGE_SIZE,
  onRowsPerPageChange,
  totals,
  onSelect,
  isLoading = false,
  hasMaxHeight = true,
  sort = null,
  onSort,
}) => {
  const [expanded, setExpanded] = useState(null);
  const safeRows = rows || [];
  const expandable = safeRows.some((row) => row.purchases || row.receipts);
  const columnCount = columns.length + (expandable ? 1 : 0);
  const totalCount = total || 0;
  const minTableWidth = columns.reduce(
    (sum, key) => sum + getColumnWidth(key),
    expandable ? EXPAND_COLUMN_WIDTH : 0
  );

  const toggleRow = (event, rowId) => {
    event.stopPropagation();
    setExpanded((prev) => (prev === rowId ? null : rowId));
  };

  return (
    <>
      <GeneralTableBox hasMaxHeight={hasMaxHeight} fixedLayout minTableWidth={minTableWidth}>
        <Table stickyHeader size="small">
          <TableHead>
            <TableRow>
              {expandable && <HeadTableCell sx={{ width: EXPAND_COLUMN_WIDTH }} />}
              {columns.map((key) => (
                <SortableHeadTableCell
                  key={key}
                  sortKey={key}
                  sort={sort}
                  onSort={onSort}
                  sx={getCellSx(key, { width: getColumnWidth(key) })}
                  title={COLUMN_LABELS[key] || key}
                >
                  {COLUMN_LABELS[key] || key}
                </SortableHeadTableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {isLoading ? (
              <TableBodySkeleton rows={5} />
            ) : safeRows.length ? (
              safeRows.map((row, index) => {
                const rowId = row.uuid || index;
                const isExpanded = expanded === rowId;
                return (
                  <Fragment key={rowId}>
                    <TableRow
                      hover
                      onClick={onSelect ? () => onSelect(row) : undefined}
                      sx={{ cursor: onSelect ? 'pointer' : 'default' }}
                    >
                      {expandable && (
                        <RowTableCell>
                          {(row.purchases || row.receipts) && (
                            <Tooltip title={isExpanded ? TEXTS.collapse : TEXTS.expand}>
                              <IconButton
                                size="small"
                                color="primary"
                                aria-expanded={isExpanded}
                                onClick={(event) => toggleRow(event, rowId)}
                              >
                                {isExpanded ? (
                                  <KeyboardArrowUp fontSize="small" />
                                ) : (
                                  <KeyboardArrowDown fontSize="small" />
                                )}
                              </IconButton>
                            </Tooltip>
                          )}
                        </RowTableCell>
                      )}
                      {columns.map((key) => {
                        const value = materialCell(key, row[key]);
                        return (
                          <RowTableCell
                            key={key}
                            sx={getCellSx(key)}
                            title={TEXT_COLUMNS.includes(key) ? String(value) : undefined}
                          >
                            {value}
                          </RowTableCell>
                        );
                      })}
                    </TableRow>
                    {isExpanded && (
                      <TableRow>
                        <RowTableCell colSpan={columnCount} sx={{ p: 0, whiteSpace: 'normal' }}>
                          <MaterialRowDetails row={row} />
                        </RowTableCell>
                      </TableRow>
                    )}
                  </Fragment>
                );
              })
            ) : (
              <NoDataSkeleton colSpan={columnCount} message={TEXTS.empty} />
            )}
            {!isLoading && totals && safeRows.length > 0 && (
              <TableRow>
                {expandable && <RowTableCell />}
                {columns.map((key, index) => (
                  <RowTableCell key={key} sx={getCellSx(key, { fontWeight: 800 })}>
                    {index === 0
                      ? TEXTS.total
                      : totals[key] !== undefined
                        ? materialCell(key, totals[key])
                        : ''}
                  </RowTableCell>
                ))}
              </TableRow>
            )}
          </TableBody>
        </Table>
      </GeneralTableBox>

      {onPage && totalCount > 0 && (
        <PaginationTable
          page={page}
          totalPages={Math.max(1, Math.ceil(totalCount / rowsPerPage))}
          totalCount={totalCount}
          rowsPerPage={rowsPerPage}
          rowsPerPageOptions={SERVER_ROWS_PER_PAGE_OPTIONS}
          onRowsPerPageChange={onRowsPerPageChange}
          onPageChange={(_, value) => onPage(value)}
        />
      )}
    </>
  );
};

export default MaterialReportTable;
